import dayjs from "dayjs";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { exportCreditDebitNotes } from "../../services";
import { CreditDebitNoteFilterParams } from "../../types/credit-debit-note.type";
import { useBlobExport } from "../Bdc/useBlobExport";
import { NOTE_EXPORT_MAX_DAYS } from "./constants";

const EXPORT_KEY = "credit-debit-notes";
/** The server names the file the same way; this is used only when its header is unreadable. */
const FILENAME_DATE_FORMAT = "YYYYMMDD";

/**
 * Why the current note-date range cannot be exported, or null when it can: both days picked and
 * at most NOTE_EXPORT_MAX_DAYS apart, both ends counted (the server's own cap).
 */
export const noteExportRangeError = (
  dateFrom: string | undefined,
  dateTo: string | undefined,
): string | null => {
  if (!dateFrom || !dateTo) {
    return `เลือกวันที่เอกสาร ตั้งแต่–ถึง (ไม่เกิน ${NOTE_EXPORT_MAX_DAYS} วัน) เพื่อ Export`;
  }
  const dayCount = dayjs(dateTo).diff(dayjs(dateFrom), "day") + 1;
  return dayCount > NOTE_EXPORT_MAX_DAYS ? `ช่วงวันที่ต้องไม่เกิน ${NOTE_EXPORT_MAX_DAYS} วัน` : null;
};

const fallbackFilename = (dateFrom: string, dateTo: string) =>
  `credit-debit-notes-${dayjs(dateFrom).format(FILENAME_DATE_FORMAT)}_${dayjs(dateTo).format(
    FILENAME_DATE_FORMAT,
  )}.xlsx`;

/**
 * Head office pulls the listed notes into Excel (CN negative, DN positive) with the list's
 * current filters — for credit shops' billing, among others. The interceptor reports a refusal.
 */
export default function NoteExportButton({
  params,
}: Readonly<{ params: CreditDebitNoteFilterParams }>) {
  const { busy, run } = useBlobExport();
  const { date_from: dateFrom, date_to: dateTo } = params;
  const rangeError = noteExportRangeError(dateFrom, dateTo);

  const handleExport = () => {
    if (!dateFrom || !dateTo || rangeError) return;
    run(
      EXPORT_KEY,
      () => exportCreditDebitNotes({ ...params, date_from: dateFrom, date_to: dateTo }),
      fallbackFilename(dateFrom, dateTo),
    );
  };

  return (
    <Stack alignItems="flex-end" gap={0.5}>
      <Button
        variant="outlined"
        startIcon={<FileDownloadIcon />}
        loading={busy === EXPORT_KEY}
        disabled={Boolean(rangeError)}
        onClick={handleExport}
      >
        Export Excel
      </Button>
      {rangeError && (
        <Typography variant="caption" color="text.secondary">
          {rangeError}
        </Typography>
      )}
    </Stack>
  );
}
